import React, { memo, useContext } from 'react';
import isEqual from 'lodash.isequal';
import { Box } from '@hz-design-system/web-ui';

import { imageFormatter } from '@/client/utils/formatters';
import ListingVipLink from '@/client/components/Listings/atoms/ListingVipLink';
import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import { Attribute } from '@/client/types/Listings';
import { TImage } from '@/client/types/Images';
import {
  ListingAttributes,
  ListingSellerInfoPriority,
  ListingTrustIndicators,
} from '@/client/components/ListingsNew/ListingListViewContentHorizontal/ListingListViewTablet/components';
import {
  ListingDescription,
  ListingFigure,
  ListingPrice,
  ListingTitle,
  ListingUrgencyBadge,
} from '@/client/components/ListingsNew/components';
import Classes from './ListingListViewContentTablet.module.scss';
import experiments from '@/utils/labs/experiments';

type TListingListViewContentTablet = {
  carPassUrl?: Attribute;
  categories?: TCategories;
  correlationId?: string;
  date: string;
  decodingType?: 'sync' | 'async' | 'auto';
  description: string;
  energyLabel?: Attribute;
  extendedAttributes?: Attribute[];
  fetchPriority?: 'high' | 'low' | 'auto';
  isFavorite?: boolean;
  isVerifiedSeller: boolean;
  loadingType?: 'eager' | 'lazy';
  location: { label: string; distance?: string };
  mileage?: Attribute;
  napAvailable?: boolean;
  onFavorite: () => void;
  opvalStickerText?: string;
  pageLocation?: string;
  pictures?: TImage[];
  price: string;
  priorityProduct?: string;
  regularAttributes?: Attribute[];
  sellerInformation?: TSellerInformation;
  thinContent?: boolean;
  title: string;
  trackingData?: string;
  trustIndicators?: { value?: string; key: string }[];
  urgencyFeatureActive?: boolean;
  vipUrl: string;
};

const ListingListViewContentTablet = ({
  carPassUrl,
  categories,
  correlationId,
  date,
  decodingType,
  description,
  energyLabel,
  extendedAttributes = [],
  fetchPriority,
  isFavorite = false,
  isVerifiedSeller,
  loadingType,
  location,
  mileage,
  napAvailable = false,
  onFavorite,
  opvalStickerText,
  pageLocation,
  pictures,
  price,
  priorityProduct = '',
  regularAttributes = [],
  sellerInformation,
  thinContent,
  title,
  trackingData,
  trustIndicators = [],
  urgencyFeatureActive = false,
  vipUrl,
}: TListingListViewContentTablet) => {
  const { tenantContext, labsConfig } = useContext(EnvironmentContext);
  const { isListViewFavoriteButtonEnabled } = experiments({ labsConfig });

  return (
    <>
      <ListingVipLink
        vipUrl={vipUrl}
        trackingData={trackingData}
        correlationId={correlationId}
        pageLocation={pageLocation}
        thinContent={thinContent}
        className={Classes['hz-Listing-coverLink']}
      >
        <ListingFigure
          images={imageFormatter(pictures ?? [], title, categories, extendedAttributes)}
          opvalStickerText={opvalStickerText}
          fetchPriority={fetchPriority}
          loadingType={loadingType}
          decodingType={decodingType}
          isFavorite={isFavorite}
          onFavorite={onFavorite}
          {...(isListViewFavoriteButtonEnabled && { showFavoriteIcon: true })}
        />

        <Box as="div" name="Listing-group--tablet" className={Classes['hz-Listing-group']}>
          <Box as="div" name="Listing-group--details-tablet" className={Classes['hz-Listing-group--details']}>
            <ListingTitle.Large title={title} strong={true} />
            <ListingPrice price={price} />
            <ListingDescription.Regular description={description} />
            <ListingTrustIndicators trustIndicators={trustIndicators} />
            {urgencyFeatureActive && (
              <Box as="div" name="Listing-urgency-badge-tablet">
                <ListingUrgencyBadge />
              </Box>
            )}
          </Box>
          <ListingAttributes
            regularAttributes={regularAttributes}
            mileage={mileage}
            carPassUrl={carPassUrl}
            napAvailable={napAvailable}
            energyLabel={energyLabel}
            tenantContext={tenantContext}
          />
        </Box>
      </ListingVipLink>

      <ListingSellerInfoPriority
        sellerInformation={sellerInformation}
        isVerifiedSeller={isVerifiedSeller}
        location={location}
        date={date}
        priorityProduct={priorityProduct}
      />
    </>
  );
};

// todo: find which properties are really needs to be checked for preventing rerendering
function arePropsEqual(prevProps, nextProps) {
  return isEqual(prevProps, nextProps);
}

export default memo(ListingListViewContentTablet, arePropsEqual);
